/**
 * Centralized Logging Service for Hudan Lil Muttaqeen
 * Provides comprehensive logging with different levels, categories, and persistence
 */

// Log levels
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

// Log categories for filtering
export type LogCategory =
  | 'auth'        // Authentication events
  | 'api'         // API requests/responses
  | 'navigation'  // Page navigation
  | 'user_action' // User interactions (clicks, form submissions)
  | 'error'       // Errors and exceptions
  | 'performance' // Performance metrics
  | 'system'      // System events
  | 'custom';     // Custom logs

// Log entry structure
export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: Record<string, unknown>;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  ip?: string;
  duration?: number;
  stack?: string;
}

// Logger configuration
interface LoggerConfig {
  enableConsole: boolean;
  enableLocalStorage: boolean;
  enableRemote: boolean;
  minLevel: LogLevel;
  maxLocalStorageEntries: number;
  remoteEndpoint?: string;
  batchSize: number;
  flushInterval: number;
}

// Default configuration
const defaultConfig: LoggerConfig = {
  enableConsole: import.meta.env.DEV,
  enableLocalStorage: true,
  enableRemote: import.meta.env.DEV,
  minLevel: import.meta.env.DEV ? 'debug' : 'info',
  maxLocalStorageEntries: 500,
  remoteEndpoint: 'hudan_lms.api.logs.record_log',
  batchSize: 10,
  flushInterval: 30000, // 30 seconds
};

// Log level priorities
const levelPriority: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  fatal: 4,
};

// Storage key
const STORAGE_KEY = 'hudan_logs';
const SESSION_ID_KEY = 'hudan_session_id';

class Logger {
  private config: LoggerConfig;
  private pendingLogs: LogEntry[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    this.sessionId = this.getOrCreateSessionId();
    this.startFlushTimer();
    this.setupGlobalErrorHandlers();
  }

  private getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }

  private generateId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return levelPriority[level] >= levelPriority[this.config.minLevel];
  }

  private getUserInfo(): { userId?: string; userEmail?: string; userRole?: string } {
    try {
      const userData = localStorage.getItem('frappe_user_data');
      if (userData) {
        const user = JSON.parse(userData);
        return {
          userId: user.email,
          userEmail: user.email,
          userRole: user.role,
        };
      }
    } catch {
      // Ignore parsing errors
    }
    return {};
  }

  private createEntry(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: Record<string, unknown>,
    error?: Error
  ): LogEntry {
    const userInfo = this.getUserInfo();

    return {
      id: this.generateId(),
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      ...userInfo,
      sessionId: this.sessionId,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      stack: error?.stack,
    };
  }

  private logToConsole(entry: LogEntry): void {
    if (!this.config.enableConsole) return;

    const style = {
      debug: 'color: #6b7280',
      info: 'color: #3b82f6',
      warn: 'color: #f59e0b',
      error: 'color: #ef4444',
      fatal: 'color: #dc2626; font-weight: bold',
    };

    const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}] [${entry.category}]`;

    console.groupCollapsed(`%c${prefix} ${entry.message}`, style[entry.level]);
    if (entry.data) {
      console.log('Data:', entry.data);
    }
    if (entry.userId) {
      console.log('User:', entry.userEmail, `(${entry.userRole})`);
    }
    if (entry.stack) {
      console.log('Stack:', entry.stack);
    }
    console.groupEnd();
  }

  private saveToLocalStorage(entry: LogEntry): void {
    if (!this.config.enableLocalStorage) return;

    try {
      const logs = this.getLocalLogs();
      logs.push(entry);

      // Keep only the last N entries
      while (logs.length > this.config.maxLocalStorageEntries) {
        logs.shift();
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
    } catch (e) {
      // Storage might be full, clear old entries
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.setItem(STORAGE_KEY, JSON.stringify([entry]));
      } catch {
        // Ignore if still failing
      }
    }
  }

  private async sendToRemote(entries: LogEntry[]): Promise<void> {
    if (!this.config.enableRemote || !this.config.remoteEndpoint) return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';

      await fetch(`${apiUrl}/api/method/${this.config.remoteEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ logs: entries }),
      });
    } catch {
      // Silently fail - don't log errors about logging
    }
  }

  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);

    // Also flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;

    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      this.error('error', 'Unhandled Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }, event.error);
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('error', 'Unhandled Promise Rejection', {
        reason: event.reason?.message || String(event.reason),
      });
    });
  }

  // Public methods
  log(level: LogLevel, category: LogCategory, message: string, data?: Record<string, unknown>, error?: Error): void {
    if (!this.shouldLog(level)) return;

    const entry = this.createEntry(level, category, message, data, error);

    this.logToConsole(entry);
    this.saveToLocalStorage(entry);

    // Add to pending batch for remote sending
    if (this.config.enableRemote) {
      this.pendingLogs.push(entry);

      // Flush immediately for errors
      if (level === 'error' || level === 'fatal' || this.pendingLogs.length >= this.config.batchSize) {
        this.flush();
      }
    }
  }

  debug(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('debug', category, message, data);
  }

  info(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('info', category, message, data);
  }

  warn(category: LogCategory, message: string, data?: Record<string, unknown>): void {
    this.log('warn', category, message, data);
  }

  error(category: LogCategory, message: string, data?: Record<string, unknown>, error?: Error): void {
    this.log('error', category, message, data, error);
  }

  fatal(category: LogCategory, message: string, data?: Record<string, unknown>, error?: Error): void {
    this.log('fatal', category, message, data, error);
  }

  // Convenience methods for specific categories
  auth(message: string, data?: Record<string, unknown>): void {
    this.info('auth', message, data);
  }

  api(message: string, data?: Record<string, unknown>): void {
    this.debug('api', message, data);
  }

  navigation(message: string, data?: Record<string, unknown>): void {
    this.info('navigation', message, data);
  }

  userAction(message: string, data?: Record<string, unknown>): void {
    this.info('user_action', message, data);
  }

  performance(message: string, data?: Record<string, unknown>): void {
    this.debug('performance', message, data);
  }

  // Flush pending logs to remote
  async flush(): Promise<void> {
    if (this.pendingLogs.length === 0) return;

    const logsToSend = [...this.pendingLogs];
    this.pendingLogs = [];

    await this.sendToRemote(logsToSend);
  }

  // Get logs from local storage
  getLocalLogs(): LogEntry[] {
    try {
      const logs = localStorage.getItem(STORAGE_KEY);
      return logs ? JSON.parse(logs) : [];
    } catch {
      return [];
    }
  }

  // Filter logs by criteria
  filterLogs(options: {
    level?: LogLevel;
    category?: LogCategory;
    startDate?: Date;
    endDate?: Date;
    search?: string;
  }): LogEntry[] {
    let logs = this.getLocalLogs();

    if (options.level) {
      logs = logs.filter(l => l.level === options.level);
    }

    if (options.category) {
      logs = logs.filter(l => l.category === options.category);
    }

    if (options.startDate) {
      logs = logs.filter(l => new Date(l.timestamp) >= options.startDate!);
    }

    if (options.endDate) {
      logs = logs.filter(l => new Date(l.timestamp) <= options.endDate!);
    }

    if (options.search) {
      const searchLower = options.search.toLowerCase();
      logs = logs.filter(l =>
        l.message.toLowerCase().includes(searchLower) ||
        JSON.stringify(l.data).toLowerCase().includes(searchLower)
      );
    }

    return logs;
  }

  // Clear local logs
  clearLocalLogs(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.getLocalLogs(), null, 2);
  }

  // Download logs as file
  downloadLogs(filename?: string): void {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `hudan_logs_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Get session ID
  getSessionId(): string {
    return this.sessionId;
  }

  // Update configuration
  updateConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    if (config.flushInterval) {
      this.startFlushTimer();
    }
  }
}

// Create singleton instance
export const logger = new Logger();

// Timer helper for performance logging
export function createTimer(name: string): () => void {
  const start = performance.now();
  return () => {
    const duration = performance.now() - start;
    logger.performance(`Timer: ${name}`, { duration: Math.round(duration) });
  };
}

// API logging helper
export function logApiCall(
  method: string,
  endpoint: string,
  requestData?: Record<string, unknown>
): (response?: unknown, error?: Error) => void {
  const start = performance.now();

  logger.api(`API Request: ${method} ${endpoint}`, {
    method,
    endpoint,
    requestData,
  });

  return (response?: unknown, error?: Error) => {
    const duration = performance.now() - start;

    if (error) {
      logger.error('api', `API Error: ${method} ${endpoint}`, {
        method,
        endpoint,
        duration: Math.round(duration),
        error: error.message,
      }, error);
    } else {
      logger.api(`API Response: ${method} ${endpoint}`, {
        method,
        endpoint,
        duration: Math.round(duration),
        response: typeof response === 'object' ? response : { value: response },
      });
    }
  };
}

export default logger;
